import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import Dialog from 'components/dialog';
import { logger } from 'utils/logger';

import { AvailableRestaurantDialogBody } from './components/available-restaurant-dialog-body';
import { DialogHeader } from './components/header';
import { UnavailableRestaurantDialogBody } from './components/unavailable-restaurant-dialog-body';
import { usePriceOrderFailureDialog } from './hooks/use-price-order-failure';
import { IPriceOrderFailureDialogProps } from './types';

/**
 * This component is used to display a dialog that allows the user to select a nearby restaurant.
 * It is used when the user's order cannot be completed at the current restaurant.
 * If the user doesn't have permission to access their location we will use the closed store's location to find a nearby restaurant.
 * Also if there is not an available restaurant we will display a message to the user to just go to the store locator to find a nearby restaurant.
 */
export const PriceOrderFailureDialog: React.FC<IPriceOrderFailureDialogProps> = ({
  onClose,
  additionalLoggingContext,
  title,
  body,
}) => {
  const { formatMessage } = useIntl();

  const {
    isDialogOpen,
    selectNearbyStore,
    chooseAnotherStore,
    availableRestaurant,
  } = usePriceOrderFailureDialog({
    onClose,
  });

  const confirmButtonLabel = availableRestaurant?._id
    ? formatMessage({ id: 'restaurantClosedDialogButton' })
    : undefined;

  useEffect(() => {
    if (isDialogOpen) {
      logger.error({
        message: 'Cart Error: Order could not be completed',
        isErrorModal: true,
        context: {
          ...additionalLoggingContext,
          guestMessageTitle: title || 'Cart Error: Order could not be completed',
          isOrderCouldNotBeCompletedError: true,
        },
      });
    }
  }, [isDialogOpen, additionalLoggingContext, title]);

  return (
    <Dialog
      modalAppearanceEventMessage="RestaurantClosedDialog"
      showDialog={isDialogOpen}
      confirmButtonLabel={confirmButtonLabel}
      closeButtonLabelId="restaurantClosedDialogButtonCancel"
      confirmDialog={selectNearbyStore}
      onDismiss={chooseAnotherStore}
      showCloseButton
      closeButtonVariant={ActionButtonVariants.OUTLINE}
      headingComponent={
        <DialogHeader title={title ?? formatMessage({ id: 'restaurantClosedDialogHeading' })} />
      }
      bodyComponent={
        availableRestaurant ? (
          <AvailableRestaurantDialogBody
            body={body}
            availableRestaurant={availableRestaurant}
            formatMessage={formatMessage}
          />
        ) : (
          <UnavailableRestaurantDialogBody body={body} formatMessage={formatMessage} />
        )
      }
    />
  );
};
